import React from 'react';
import { X, Mail, Phone, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface ContactHumanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactHumanModal: React.FC<ContactHumanModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  if (!isOpen) return null;

  const wellbeingOfficerEmail = user?.clientConfig?.wellbeingOfficerEmail;
  const wellbeingOfficerPhone = user?.clientConfig?.wellbeingOfficerPhone;
  const bookingLink = user?.clientConfig?.bookingLink;

  const handleBookingClick = () => {
    if (bookingLink) {
      window.open(bookingLink, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div 
          className="bg-white rounded-card shadow-card max-w-md w-full p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <h2 className="text-2xl font-semibold text-rm-ink-900 mb-4">
            Contact Human Support
          </h2>

          {/* Content */}
          <div className="space-y-4">
            <p className="text-rm-ink-700 text-sm">
              Need to speak with someone? Here's how you can reach our wellbeing officer:
            </p>

            {/* Email */}
            {wellbeingOfficerEmail && (
              <div className="flex items-start gap-3 p-3 bg-rm-bg-50 rounded-lg border border-rm-neutral-600/10">
                <Mail className="w-5 h-5 text-rm-primary-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-rm-neutral-600 mb-1">Email</div>
                  <a
                    href={`mailto:${wellbeingOfficerEmail}`}
                    className="text-rm-accent-600 hover:text-rm-accent-500 underline font-medium transition-colors break-all"
                  >
                    {wellbeingOfficerEmail}
                  </a>
                </div>
              </div>
            )}

            {/* Phone */}
            {wellbeingOfficerPhone && (
              <div className="flex items-start gap-3 p-3 bg-rm-bg-50 rounded-lg border border-rm-neutral-600/10">
                <Phone className="w-5 h-5 text-rm-primary-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-rm-neutral-600 mb-1">Phone</div>
                  <a
                    href={`tel:${wellbeingOfficerPhone}`}
                    className="text-rm-accent-600 hover:text-rm-accent-500 underline font-medium transition-colors"
                  >
                    {wellbeingOfficerPhone}
                  </a>
                </div>
              </div>
            )}

            {/* Booking button */}
            {bookingLink && (
              <div className="pt-2">
                <button
                  onClick={handleBookingClick}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-rm-accent-600 hover:bg-rm-accent-500 text-white rounded-lg font-medium transition-colors shadow-soft"
                  aria-label="Book a meeting"
                >
                  <Calendar className="w-5 h-5" />
                  Book a meeting
                </button>
              </div>
            )}

            {/* No contact info message */}
            {!wellbeingOfficerEmail && !wellbeingOfficerPhone && !bookingLink && (
              <div className="text-center py-4 text-rm-neutral-600">
                Contact information is not currently available. Please check back later.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
